import { Badge, Card, Chip, Group, Loader, Paper, Stack, Text, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { useInViewport } from "@mantine/hooks";
import { GridColDef } from "@mui/x-data-grid";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { Fragment, useEffect, useState } from "react";
import { useStopwatch } from "react-timer-hook";

import { DataGridBase } from "@/components/DataGridBase";
import { InfoCard } from "@/components/InfoCard";
import { Plate } from "@/components/Plate";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSession } from "@/hooks";
import { QueueByDay, Role, useGetCompaniesQuery, useLazyGetQueueByCompanyAndShippingCompanyQuery, useLazyGetSettingsByCompanyQuery } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_base/fila/")({
  component: RouteComponent,
  beforeLoad: requireRoles({ allowed: [Role.Transportadora] })
});

function RouteComponent() {
  const { colors } = useMantineTheme();
  const { shippingCompanyId } = useSession();
  const { data: companies, isLoading } = useGetCompaniesQuery();
  const [getQueue, { data: queueData, isFetching }] = useLazyGetQueueByCompanyAndShippingCompanyQuery();
  const [getSettingsByCompany, { data: settingsByCompany }] = useLazyGetSettingsByCompanyQuery();
  const [companyId, setCompanyId] = useState("");

  const isInTolerance = settingsByCompany?.enableTolerance;
  const toleranceTime = settingsByCompany?.tolerance;

  const refetch = () => {
    if (companyId) {
      getQueue({ ids: { company: Number(companyId), shippingCompany: shippingCompanyId } });
    }
  };

  useEffect(() => {
    if (!isLoading && companies && companies.length > 0) {
      setCompanyId(String(companies[0].id));
    }
  }, [isLoading]);

  useEffect(() => {
    refetch();

    if (companyId) {
      getSettingsByCompany({ id: Number(companyId) });
    }
  }, [companyId]);

  const columns: GridColDef<QueueByDay["queues"][number]>[] = [
    {
      field: "id",
      headerName: "ID"
    },
    {
      field: "positionInfo",
      headerName: "Posição",
      type: "custom",
      display: "flex",
      renderCell: ({ row }) => {
        const position = `${row.position}°`;
        const date = dayjs(row.date).format("HH:mm");

        return <InfoCard color={colors.red[9]} labels={{ top: position, bottom: date }} />;
      }
    },
    {
      field: "placa",
      headerName: "Placa",
      display: "flex",
      minWidth: 175,
      type: "string",
      valueGetter: (val, { truck }) => truck.licensePlate,
      renderCell: ({ row }) => {
        return <Plate plate={row.truck.licensePlate} color="text-red-600" bgColor="bg-transparent" />;
      }
    },
    {
      field: "info",
      headerName: "Informações",
      type: "custom",
      display: "flex",
      flex: 1,
      minWidth: 500,
      renderCell: ({ row }) => {
        const { driver, scheduling } = row;
        const driverName = driver.name;
        const customerName = scheduling?.order?.customer?.name || "N/A";
        const netCapacity = scheduling?.truck.netCapacity || 0;
        const date = dayjs(row.date).format("DD/MM/YYYY HH:mm");

        const getTolerance = () => {
          if (isInTolerance) return "N/A";
          if (!toleranceTime) return "N/A";

          const [hours, minutes] = toleranceTime.split(":").map(Number);
          const date = dayjs(row.date).subtract(hours, "hour").subtract(minutes, "minute");

          return date.format("HH:hh");
        };

        const styles = {
          wrapper: cn("inline-grid h-auto grid-cols-[1fr_auto_auto] gap-2 py-2"),
          item: cn("overflow-hidden p-2 text-ellipsis whitespace-nowrap")
        };

        const Item = ({ label, value, timer = false }: { label: string; value?: string | number; timer?: boolean }) => {
          const { ref } = useInViewport();

          const { reset } = useStopwatch({ autoStart: timer });

          useEffect(() => {
            return reset;
          }, []);

          if (!value) return null;

          return (
            <Paper className={styles.item} ref={ref} withBorder>
              <Text size="sm" fw="bold">
                {label}
              </Text>

              <Text size="sm">{value}</Text>
            </Paper>
          );
        };

        return (
          <div className={styles.wrapper}>
            <Item label="Motorista:" value={driverName} />
            <Item label="Previsão:" value={date} />
            <Item label="Tolerância:" value={getTolerance()} timer />
            <Item label="Cliente:" value={customerName} />
            <Item label="Pedido:" value={row.scheduling?.order.code} />
            <Item label="Capacidade:" value={`${netCapacity}t`} />
          </div>
        );
      }
    },
    {
      field: "order",
      headerName: "Pedido",
      align: "center",
      headerAlign: "center",
      valueGetter: (val, { scheduling }) => scheduling?.order.code || "N/A"
    },
    {
      field: "position",
      headerName: "Posição",
      align: "center",
      headerAlign: "center",
      //TODO pedir para a API retornar a posição na fila
      // @ts-expect-error Apenas preciso que a propriedade position exista
      valueGetter: (val, { scheduling }) => scheduling?.position || "N/A"
    },
    {
      field: "driver",
      headerName: "Motorista",
      flex: 1,
      minWidth: 300,
      type: "string",
      display: "flex",
      valueGetter: (val, { driver }) => driver.name,
      renderCell: ({ row }) => {
        //TODO lembrar de pedir que a propriedade freelancer seja retornada na API
        const { driver /* freelancer */ } = row;

        return (
          <Stack>
            {/* {freelancer && <Badge color="yellow">Freelancer</Badge>} */}
            <Text size="sm">{driver.name}</Text>
          </Stack>
        );
      }
    },
    {
      field: "scheduling",
      headerName: "Cliente",
      flex: 1,
      minWidth: 200,
      type: "string",
      valueGetter: (val, { scheduling }) => scheduling?.order?.customer?.name || "N/A"
    },
    {
      field: "netCapacity",
      headerName: "Capacidade Líquida",
      flex: 1,
      minWidth: 200,
      type: "string",
      valueGetter: (val, { scheduling }) => `${scheduling?.truck?.netCapacity || 0}t`
    }
  ];

  return (
    <Stack>
      <div className="flex items-center justify-between gap-[inherit]">
        <div className="flex items-center gap-[inherit]">
          {isLoading && <Loader />}
          <DashboardComponents.Title>Fila de Caminhões </DashboardComponents.Title>
        </div>

        <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Fila" }} onClick={refetch} loading={isFetching} />
      </div>

      <Chip.Group multiple={false} value={companyId} onChange={setCompanyId}>
        <Group>
          {companies?.map((company) => (
            <Chip variant="outline" value={company.id} checked={Number(companyId) === company.id} key={company.id}>
              {company.name}
            </Chip>
          ))}
        </Group>
      </Chip.Group>

      <Group justify="center">{isFetching && <Loader size="sm" type="nextCargo" />}</Group>

      {!isFetching && queueData && (
        <Stack>
          {queueData.grouped.length > 0 &&
            queueData.grouped.map((queueByDay, i) => (
              <Fragment key={i}>
                <div className="flex items-center justify-end gap-2 self-end rounded-full border border-[#dee2e6] bg-white px-2 py-1 dark:border-[#424242] dark:bg-[#2e2e2e]">
                  <Tooltip label="Data">
                    <Badge size="xl">{queueByDay.date}</Badge>
                  </Tooltip>

                  <Tooltip label="Total de caminhões na fila neste dia">
                    <Badge size="lg" color="blue" circle>
                      {queueByDay.length}
                    </Badge>
                  </Tooltip>
                </div>

                <Card withBorder key={i}>
                  <DataGridBase
                    rows={queueByDay.queues}
                    columns={columns}
                    loading={isFetching}
                    columnVisibilityModel={{
                      id: false,
                      driver: false,
                      order: false,
                      position: false,
                      scheduling: false,
                      netCapacity: false
                    }}
                    rowHeight={120}
                  />
                </Card>
              </Fragment>
            ))}

          {queueData.grouped.length === 0 && (
            <Title ta="center" order={3}>
              Nenhuma fila encontrada.
            </Title>
          )}

          {/* <pre>{JSON.stringify(queueData, null, 2)}</pre> */}
        </Stack>
      )}
    </Stack>
  );
}
